import { useEffect, useState, useCallback, useContext } from "react";
import { CiSearch } from "react-icons/ci";
import { CiCirclePlus } from "react-icons/ci";
import toast from "react-hot-toast";
import "../../styles/outletUsers.css";
import Loader from "../../components/Loader";
import NoData from "../../components/NoData";
import AddOutletUserModal from "./modals/AddOutletUserModal";
import OutletUsersServices from "../../services/OutletUsersServices";
import { getAvatarColor, getInitials } from "../../utils/utils";
import { MdEdit, MdDelete, MdLockReset } from "react-icons/md";
import { LiaWalletSolid } from "react-icons/lia";
import EditOutletModal from "./modals/EditOutletModal";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import AddWalletAmountModal from "./modals/AddWalletAmountModal";
import AdminUsersServices from "../../services/AdminUsersServices";
import { AuthContext } from "../../context/AuthContext";
import ResetPasswordModal from "../../components/ResetPasswordModal";
import PasswordServices from "../../services/PasswordServices";
import ConfirmUnblockModal from "../../components/ConfirmUnblockModal";
import { MdLockOpen } from "react-icons/md";
import { MdImage } from "react-icons/md";
import AssignTemplateModal from "../../components/AssignTemplateModal";

const OutLetManage = () => {
  const outletSerApi = new OutletUsersServices();
  const adminSerApi = new AdminUsersServices();
  const passwordApi = new PasswordServices();

  const { user } = useContext(AuthContext);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [outletUserList, setOutletUserList] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchFilter, setSearchFilter] = useState("");
  const [pagination, setPagination] = useState(null);
  const [editOutletModal, setEditOutletModal] = useState(false);
  const [editOutletData, setEditOutletData] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [walletModal, setWalletModal] = useState(false);
  const [walletUser, setWalletUser] = useState(null);
  const [adminWalletBalance, setAdminWalletBalance] = useState(0.0);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [unblockModalOpen, setUnblockModalOpen] = useState(false);
  const [unblockUser, setUnblockUser] = useState(null);
  const [unblockLoading, setUnblockLoading] = useState(false);
  const [assignTplModal, setAssignTplModal] = useState(false);
  const [assignTplUser, setAssignTplUser] = useState(null);

  const getAdminWalletBalance = useCallback(async () => {
    try {
      const response = await adminSerApi.addAdminWalletBalanceById();

      if (response.isSuccess) {
        setAdminWalletBalance(response?.data?.wallet_balance);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Failed to get admin wallet balance");
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchFilter]);

  const getAllOutletUserData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await outletSerApi.getAllOutletuserData(
        page,
        limit,
        searchFilter,
      );

      if (response.isSuccess) {
        setOutletUserList(response.data || []);
        setPagination(response.pagination);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Failed to load outlet users");
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchFilter]);

  const toggleUserStatus = async (userData) => {
    const newStatus = userData.is_active === 1 ? 0 : 1;

    try {
      const response = await outletSerApi.toggleOutletStatus({
        id: userData.id,
        is_active: newStatus,
      });

      if (response.isSuccess) {
        toast.success(response.message);
        setOutletUserList((prev) =>
          prev.map((u) =>
            u.id === userData.id ? { ...u, is_active: newStatus } : u,
          ),
        );
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message || "Failed to update user status";
      toast.error(msg);
    }
  };

  const openEditModal = async (userData) => {
    setEditOutletModal(true);
    setEditOutletData(userData);
  };
  const confirmDelete = async (userData) => {
    setSelectedUser(userData);
    setDeleteModalOpen(true);
  };

  const openWalletModal = (userData) => {
    setWalletUser(userData);
    setWalletModal(true);
  };

  const openResetPasswordModal = (userData) => {
    setResetUser(userData);
    setResetModalOpen(true);
  };

  const openUnblockModal = (userData) => {
    setUnblockUser(userData);
    setUnblockModalOpen(true);
  };

  const openAssignTemplate = (userData) => {
    setAssignTplUser(userData);
    setAssignTplModal(true);
  };

  const closeAssignTemplate = () => {
    setAssignTplModal(false);
    setAssignTplUser(null);
  };

  const handleAssignTemplate = (payload) => outletSerApi.assignTemplate(payload);
  const handleSearch = (e) => {
    setPage(1); // reset page
    setSearchFilter(e.target.value);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    outletSerApi
      .deleteOutletUser(selectedUser.id)
      .then((response) => {
        if (response.isSuccess) {
          toast.success(response.message);
          getAdminWalletBalance();
          getAllOutletUserData();
        } else {
          toast.error(response.message);
        }
      })
      .catch((error) => {
        console.error("Error deleteOutletUser:", error);
        const backendMessage =
          error?.response?.data?.message || "Failed to delete outlet user";

        toast.dismiss();
        toast.error(backendMessage);
      })
      .finally(() => {
        setDeleteLoading(false);
        setDeleteModalOpen(false);
        setSelectedUser(null);
      });
  };

  const handleResetPassword = async (newPassword) => {
    if (!resetUser) return;
    setResetLoading(true);
    try {
      const response = await passwordApi.resetUserPassword({
        userId: resetUser.id,
        newPassword,
        description: `Outlet password reset for user ${resetUser.username}`,
      });
      if (response.isSuccess) {
        toast.success(response.message || "Password reset successfully");
        setResetModalOpen(false);
        setResetUser(null);
      } else {
        toast.error(response.message || "Failed to reset password");
      }
    } catch (error) {
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to reset password";
      toast.error(backendMessage);
    } finally {
      setResetLoading(false);
    }
  };

  const handleUnblockUser = async () => {
    if (!unblockUser) return;
    setUnblockLoading(true);
    try {
      const response = await outletSerApi.unblockOutletUser({
        id: unblockUser.id,
      });
      if (response.isSuccess) {
        toast.success(response.message || "User unblocked successfully");
        setOutletUserList((prev) =>
          prev.map((u) =>
            u.id === unblockUser.id ? { ...u, cancel_count: 0 } : u,
          ),
        );
        setUnblockModalOpen(false);
        setUnblockUser(null);
      } else {
        toast.error(response.message || "Failed to unblock user");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to unblock user",
      );
    } finally {
      setUnblockLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role_id !== 1) {
      getAdminWalletBalance();
    }
    getAllOutletUserData();
  }, [getAllOutletUserData]);

  const Avatar = ({ name, image }) => {
    if (image) {
      return <img src={image} alt={name} className="admin-avatar" />;
    }

    const initials = getInitials(name);
    const bgColor = getAvatarColor(name);

    return (
      <div
        className="admin-avatar fallback-avatar"
        style={{ backgroundColor: bgColor }}
      >
        {initials}
      </div>
    );
  };

  return (
    <div className="outlet-manage-view">
      <div className="admin_user-header">
        <div className="admin-header-row">
          {/* SEARCH */}
          <div className="admin-search">
            <div className="search-box">
              <CiSearch size={18} className="search-icon" />
              <input
                type="text"
                className="form-control search-input"
                placeholder="Search outlet"
                value={searchFilter}
                onChange={handleSearch}
              />
            </div>
          </div>

          {/* ACTION */}
          <div>
            {user?.role_id !== 1 && (
              <div>Wallet balance : {adminWalletBalance}</div>
            )}
            <button
              className="add_admin-btn"
              onClick={() => {
                setIsOpen(true);
              }}
            >
              <CiCirclePlus size={18} />
              Add Outlet
            </button>
          </div>
        </div>
      </div>

      <div>
        {loading ? (
          <Loader message="Loading Outlet Users..." />
        ) : outletUserList.length === 0 ? (
          <NoData
            message="No Outlet Users Found"
            subText="You haven't added any outlet users yet. Click on Add Outlet to get started."
          />
        ) : (
          <>
            <div className="admin-table-header">
              <div className="col-user">User</div>
              <div className="col-mobile">Mobile</div>
              <div className="col-status">Status</div>
              <div className="col-role">Created By</div>
              <div className="col-action">Action</div>
            </div>

            <div className="admin-table">
              {outletUserList.map((user) => (
                <div className="admin-row" key={user.id}>
                  {/* USER */}
                  <div className="admin-user">
                    <Avatar name={user.name} image={user.profile_image} />
                    <div>
                      <div className="admin-name">{user.name}</div>
                      <div className="admin-username">({user.username})</div>
                      <div className="admin-username">
                        Wallet balance:{" "}
                        <span className="wallet">
                          {" "}
                          {user?.wallet_balance ?? 0}
                        </span>
                      </div>
                      {user?.template_id && user?.template_name && (
                        <div className="admin-template-badge">
                          <MdImage size={14} />
                          <span>{user.template_name}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="admin-row-meta">
                    {/* NICK NAME */}
                    <div className="admin-col">
                      <div className="admin-username">{user.mobile}</div>
                    </div>

                    {/* ACTIVE SWITCH */}
                    <div className="admin-col">
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={user.is_active === 1}
                          onChange={() => toggleUserStatus(user)}
                        />
                        <span className="slider round"></span>
                      </label>
                    </div>

                    {/* CREATED BY */}
                    <div className="admin-col">
                      <div className="admin-username">
                        {user.created_by_name || "—"}
                      </div>
                    </div>
                  </div>

                  {/* ACTIONS */}
                  <div className="admin-actions">
                    {Number(user.cancel_count) >= 4 ? (
                      <>                       
                        <button
                          className="icon-btn unblock"
                          title="Unblock User"
                          onClick={() => openUnblockModal(user)}
                        >
                          <MdLockOpen size={18} /> Unblock
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="icon-btn edit"
                          title="Edit User"
                          onClick={() => openEditModal(user)}
                        >
                          <MdEdit size={18} />
                        </button>

                        <button
                          className="icon-btn delete"
                          title="Delete User"
                          onClick={() => confirmDelete(user)}
                        >
                          <MdDelete size={18} />
                        </button>
                        <button
                          className="icon-btn edit"
                          title="Reset Password"
                          onClick={() => openResetPasswordModal(user)}
                        >
                          <MdLockReset size={18} />
                        </button>
                        <button
                          className="icon-btn edit"
                          title="Add wallet balance"
                          onClick={() => openWalletModal(user)}
                        >
                          <LiaWalletSolid size={18} />
                        </button>
                        <button
                          className="icon-btn edit"
                          title={
                            user.template_id
                              ? "Change template"
                              : "Assign template"
                          }
                          onClick={() => openAssignTemplate(user)}
                        >
                          <MdImage size={18} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {pagination && pagination.totalPages > 1 && (
              <div className="modern-pagination">
                {/* Limit Selector */}
                <div className="mp-limit">
                  <span>Show</span>
                  <select
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>

                {/* Navigation */}
                <div className="mp-controls">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Prev
                  </button>

                  <div className="mp-info">
                    Page <strong>{pagination.page}</strong> of{" "}
                    <strong>{pagination.totalPages}</strong>
                  </div>

                  <button
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                </div>

                {/* Jump to Page */}
                <div className="mp-jump">
                  <select
                    value={page}
                    onChange={(e) => setPage(e.target.value)}
                  >
                    {Array.from({ length: pagination.totalPages }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        Page {i + 1}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <AddOutletUserModal
        modalIsOpen={modalIsOpen}
        setIsOpen={setIsOpen}
        getAllOutletUserData={getAllOutletUserData}
      />

      <EditOutletModal
        editOutletModal={editOutletModal}
        setEditOutletModal={setEditOutletModal}
        editOutletData={editOutletData}
        setEditOutletData={setEditOutletData}
        getAllOutletUserData={getAllOutletUserData}
      />

      <ConfirmDeleteModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Outlet"
        message={`Are you sure you want to delete "${selectedUser?.name}"?`}
        loading={deleteLoading}
      />

      <AddWalletAmountModal
        walletModal={walletModal}
        setWalletModal={setWalletModal}
        walletUser={walletUser}
        setWalletUser={setWalletUser}
        setOutletUserList={setOutletUserList}
        adminWalletBalance={adminWalletBalance}
        setAdminWalletBalance={setAdminWalletBalance}
        role_id={user?.role_id}
      />

      <ResetPasswordModal
        show={resetModalOpen}
        onClose={() => {
          if (resetLoading) return;
          setResetModalOpen(false);
          setResetUser(null);
        }}
        onConfirm={handleResetPassword}
        userName={resetUser?.name}
        loading={resetLoading}
        title="Reset Outlet Password"
      />

      <ConfirmUnblockModal
        show={unblockModalOpen}
        onClose={() => {
          if (unblockLoading) return;
          setUnblockModalOpen(false);
          setUnblockUser(null);
        }}
        onConfirm={handleUnblockUser}
        title="Unblock Outlet User"
        message={`Unblock "${unblockUser?.name}"? This will reset daily cancel count.`}
        loading={unblockLoading}
        confirmText="Yes, Unblock"
      />

      <AssignTemplateModal
        show={assignTplModal}
        onClose={closeAssignTemplate}
        targetUser={assignTplUser}
        onAssign={handleAssignTemplate}
        onSaved={getAllOutletUserData}
      />
    </div>
  );
};

export default OutLetManage;
